import React from "react";
import { useTranslation } from "react-i18next";
import { FlaskConical } from "lucide-react";

interface TesterBadgeProps {
  /** Show expiration date if provided */
  expiresAt?: Date | string | null;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Show full text or icon only */
  variant?: "full" | "icon";
  /** Optional className for custom styling */
  className?: string;
}

export const TesterBadge: React.FC<TesterBadgeProps> = ({
  expiresAt,
  size = "md",
  variant = "full",
  className = "",
}) => {
  const { t } = useTranslation();

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-3 py-1",
    lg: "text-base px-4 py-2",
  };

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  const getExpirationText = () => {
    if (!expiresAt) return null;

    const expDate = new Date(expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return t("tester_expired");
    } else if (daysLeft === 0) {
      return t("tester_expires_today");
    } else if (daysLeft <= 7) {
      return t("tester_expires_in_days", { days: daysLeft });
    }

    return null;
  };

  const expirationText = getExpirationText();
  const isExpiringSoon = expiresAt && expirationText;

  if (variant === "icon") {
    return (
      <div
        className={`inline-flex items-center justify-center rounded-full bg-purple-100 text-purple-700 ${sizeClasses[size]} ${className}`}
        title={t("tester_mode")}
      >
        <FlaskConical className={iconSizes[size]} />
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-md ${
        isExpiringSoon ? "bg-orange-100 text-orange-700" : "bg-purple-100 text-purple-700"
      } ${sizeClasses[size]} ${className}`}
    >
      <FlaskConical className={iconSizes[size]} />
      <span className="font-medium">{t("tester_mode")}</span>
      {expirationText && (
        <span className="text-xs opacity-80">({expirationText})</span>
      )}
    </div>
  );
};

export default TesterBadge;
